import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { AuthShell } from '@/components/auth/AuthShell';
import { InviteRequiredStop, ApplicationCodeEntry } from '@/components/apply/InviteRequiredStop';
import { EligibilityCheck, type EligibilityOutcome } from '@/components/apply/EligibilityCheck';
import { ReferralBlock } from '@/components/apply/ReferralBlock';
import { EditorialField, MonoLabel } from '@/components/editorial';
import { homeFor } from '@/lib/home-route';
import { useQuery } from '@tanstack/react-query';
import { signatureRenewalDue } from '@/lib/grants-status';
import type { GrantRequest } from '@/types/api';
import { deriveRenewalCta } from '@/lib/renewal-display';
import { RENEWAL_MINE_QUERY_KEY } from '@/lib/wizardDataSource';
import { resolveDashboardMode } from '@/lib/dashboard-mode';
import { api, APPLY_CODE_STORAGE_KEY, readApplyScreening, stashApplyCode, stashApplyInviteToken, stashApplyReferral } from '@/services/api';

// Built per render locale (#2109 R3 L8, #2133): the validation messages are
// copy, so the schema takes the bound `t` rather than holding English literals.
const makeApplySchema = (t: TFunction<'forms'>) =>
  z.object({
    first_name: z.string().min(1, t('applyLanding.validation.firstNameRequired')),
    email: z.string().email(t('applyLanding.validation.invalidEmail')),
    password: z
      .string()
      .min(8, t('applyLanding.validation.passwordMin'))
      .regex(/[A-Z]/, t('applyLanding.validation.passwordUppercase'))
      .regex(/[a-z]/, t('applyLanding.validation.passwordLowercase'))
      .regex(/[0-9]/, t('applyLanding.validation.passwordNumber')),
  });

type ApplyFormData = z.infer<ReturnType<typeof makeApplySchema>>;

// F-6 (#330 / F-038): the pre-eligibility checklist gate. sessionStorage
// (deliberately not localStorage — a shared/public machine shouldn't carry
// one family's answers into the next) so a family who continues (or skips)
// and then reloads isn't re-screened, but a fresh browser session always
// sees the checklist again.
const ELIGIBILITY_GATE_KEY = 'trf.apply.eligibility.v1';

function readEligibilityGate(): EligibilityOutcome | null {
  if (typeof window === 'undefined') return null;
  // D-FB-3 (2026-08-20): 'continued' is no longer a valid outcome — the
  // continue-past-miss path is retired. A legacy stored 'continued' from an
  // earlier session parses as null, so that family is simply re-screened.
  const stored = window.sessionStorage.getItem(ELIGIBILITY_GATE_KEY);
  const outcome = stored === 'passed' || stored === 'skipped' ? stored : null;
  if (outcome === null) return null;
  // #1014 (AP-32): this flag and the ANSWERS it stands for are separate pieces
  // of session state, and `applications.createDraft` clears the answers once a
  // draft exists. `POST /applications` is fail-closed — it refuses a create
  // carrying no screening answers — so a flag that outlived them would skip
  // the questions and then hand the family a 422 at the end of the wizard,
  // with nothing on screen they could do about it. Re-asking costs a minute.
  return readApplyScreening() ? outcome : null;
}

export function ApplyLandingPage() {
  // GH-271.1: auth surface is LOCKED (httpOnly plan) — this page only READS
  // auth state via useAuth() and calls its existing logout() action. No token
  // paths, no AuthContext changes, no storage access.
  const { applySignup, user, isAuthenticated, loading, logout } = useAuth();
  const { toast } = useToast();
  // #2109 R3 L8 (#2133): every word on this page is `forms:*` — the shared
  // /apply chrome under `applyChrome`, this page's own under `applyLanding`.
  const { t, i18n } = useTranslation('forms');
  const { t: tAuth } = useTranslation('auth');
  // L4-b / D5: remembered on this device by default, same policy as Login.
  const [rememberMe, setRememberMe] = useState(true);
  const applySchema = useMemo(() => makeApplySchema(t), [t]);
  const navigate = useNavigate();
  // #2819: the wizard sends a signed-in applicant here with this state when
  // the server would not start an application because this tab holds no
  // pre-screen answers (answered in another tab or on another device).
  const location = useLocation();
  const navState = location.state as { prescreenRequired?: unknown } | null;
  const prescreenRequired = navState?.prescreenRequired === true;
  const [submitting, setSubmitting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // #2693: a renewal-due family who follows an old /apply link while signed
  // in is offered her renewal, not a new application. Same reads and cache
  // keys as Home and /bills (`grants-mine`, `family-mine`); a parent only,
  // and never on a memorial board (Home's gate, #644/#693). Until both reads
  // settle the screen keeps its two original actions.
  const renewalReadsEnabled = isAuthenticated && user?.role === 'parent';
  const grantsQuery = useQuery({
    queryKey: ['grants-mine'],
    queryFn: () => api.grants.mine(),
    enabled: renewalReadsEnabled,
  });
  const familyQuery = useQuery({
    queryKey: ['family-mine'],
    queryFn: () => api.families.getMine(),
    enabled: renewalReadsEnabled,
  });
  // The renewal on file decides it too, the way Home and /bills do
  // (deriveRenewalCta): a renewal already pending, decided or handed off gets
  // no button here; a draft under way is offered back; a fresh one only when
  // the grant is in its due window (night shift, N7 review).
  const renewalQuery = useQuery({
    queryKey: RENEWAL_MINE_QUERY_KEY,
    queryFn: () => api.renewals.getMine(),
    enabled: renewalReadsEnabled,
    retry: false,
  });
  const renewalState = renewalQuery.isSuccess && grantsQuery.isSuccess
    ? deriveRenewalCta(renewalQuery.data?.data ?? null, (grantsQuery.data?.data ?? []) as GrantRequest[])
    : null;
  const renewalOffered = renewalState !== null && (
    renewalState.kind === 'editable'
    || renewalState.kind === 'needs_info'
    || (renewalState.kind === 'none' && signatureRenewalDue(grantsQuery.data?.data ?? []))
  );
  const renewalDue = renewalReadsEnabled
    && familyQuery.isSuccess
    && resolveDashboardMode(familyQuery.data?.data?.patient?.treatment_phase) !== 'memorial'
    && renewalOffered;

  // F-6 (#330 / F-038): null means "not yet screened this session" — shows
  // the checklist. Any of the three outcomes means "already screened" — the
  // checklist is skipped for the rest of this browser session.
  const [eligibilityGate, setEligibilityGate] = useState<EligibilityOutcome | null>(
    readEligibilityGate,
  );

  // B6 (F-013): capture a social-worker invite token from ?invite= and carry it
  // through the apply flow (stashed for api.applications.createDraft, which
  // redeems it once the wizard creates the draft). Read into state once so the
  // token survives any later URL changes on this page.
  const [searchParams, setSearchParams] = useSearchParams();
  const [inviteToken] = useState<string | null>(() => searchParams.get('invite'));

  // B6b: a PRESENT token is always checked before showing the signup form —
  // revoked/expired/used/unknown links stop here with honest copy instead of
  // silently opening the full flow (naive-user QA trust gap). States:
  // 'checking' (token present, status call in flight) → brief spinner;
  // 'invalid' → stop screen; 'ok' → form. A network failure fails OPEN (the
  // backend re-validates on draft creation, so nothing slips through).
  const [inviteCheck, setInviteCheck] = useState<'checking' | 'invalid' | 'ok'>(
    () => (inviteToken ? 'checking' : 'ok'),
  );
  // #2951, widened 2026-10-28 (Victor): the link or code has Renewal Families
  // on, so the family skips the pre-screen and goes straight to the
  // application. Either kind of code can carry it. A hint only: POST
  // /applications re-checks the credential itself before skipping the
  // screen, and the checklist below is simply not shown.
  const [credentialRenewal, setCredentialRenewal] = useState(false);

  // GH-415: a reusable application code can arrive the same way (?code=…).
  // An invite token in the URL takes precedence — when both are present the
  // code is ignored here and the backend leaves it unconsumed. Read once into
  // state (same reason as the token) so later URL changes can't drop it.
  // Hook-ordering (TDZ): every useState below precedes the effects that read it.
  // #2692: mutable only so the typo stop can drop a mistyped code (or swap in
  // the fixed one); nothing else writes it.
  const [urlCode, setUrlCode] = useState<string | null>(() =>
    searchParams.get('invite') ? null : searchParams.get('code'),
  );
  // #1017: 'typo' is the one code-specific outcome on top of the 'invalid'
  // collapse — see api.applications.codeStatus. Since 2026-10-28 a code
  // carries no cycle, so a code never reads as closed.
  const [codeCheck, setCodeCheck] = useState<'checking' | 'invalid' | 'ok' | 'typo'>(
    () => (searchParams.get('invite') ? 'ok' : searchParams.get('code') ? 'checking' : 'ok'),
  );
  // A code typed into the InviteRequiredStop entry form (already validated +
  // stashed by that component) — unlocks the signup form without a URL change.
  const [typedCode, setTypedCode] = useState<string | null>(null);
  // 2026-10-28: in `optional` mode the code box also sits above the
  // pre-screen, so a typed renewal code can skip it. A code typed there is
  // not asked for again above the signup form.
  const [codeBeforeScreen, setCodeBeforeScreen] = useState(false);

  // #1017 architecture-review addendum, C2b: the runtime, staff-settable
  // application_codes_mode (ConfigSettings, #1018) replaces the retired
  // frozen AUTH_POLICY.inviteRequired mirror (lib/auth-policy.ts). Defaults
  // OPTIMISTICALLY to 'optional' (door open) rather than blocking on the
  // network round-trip with a spinner: this gate is cosmetic-only (the
  // backend POST /api/applications is the real enforcement, same framing as
  // the invite/code checks below), so a brief flash of the signup form before
  // a `required` verdict lands and redirects is the same fail-open posture
  // already used everywhere else on this page — never a security concern,
  // only ever a UX one, and nothing has been submitted yet when it happens.
  const [gateMode, setGateMode] = useState<'required' | 'optional' | 'disabled'>('optional');
  // BLOCKER 1 (2026-08-23 addendum fix-wave): the ?code= check below must not
  // fire until the ACTUAL mode is known — the optimistic 'optional' default
  // above is a cosmetic placeholder for which door to show while /gate is in
  // flight, not a value the code-check effect may treat as real. Without this,
  // a family arriving in `disabled` mode on a stale/expired/revoked/exhausted
  // code could get checked (and dead-ended on a stop screen) before the
  // `disabled` verdict lands — the exact "door open, app says it's shut" bug
  // this fix closes. Set true in BOTH the success and failure path of the
  // gate fetch, so a network failure (which fails the mode open at 'optional')
  // still unblocks the code-check effect rather than holding it forever.
  const [gateResolved, setGateResolved] = useState(false);
  // Wave launch-w1014 L3-c (D4): /gate also says whether a new application
  // could still be submitted. After the cycle closes, the family is stopped
  // HERE — before an account and a whole form — instead of at Submit. Holds
  // the staff notice (or null). Fails open, like the mode.
  const [roundClosed, setRoundClosed] = useState<{ notice: string | null } | null>(null);
  // #3452: the cycle takes renewing families only until this Pacific day
  // (YYYY-MM-DD); null when everyone may apply. Fails open, like the mode.
  const [newFamiliesOpenOn, setNewFamiliesOpenOn] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.applications
      .gate()
      .then((res) => {
        if (cancelled) return;
        const mode = res.data?.mode;
        setGateMode(mode === 'required' || mode === 'disabled' ? mode : 'optional');
        // `accepting_submissions` / `closed_notice` (L3-c) — read through a local
        // shape until services/api.ts's gate() type carries them.
        const door = res.data as { accepting_submissions?: boolean; closed_notice?: string | null } | undefined;
        if (door?.accepting_submissions === false) setRoundClosed({ notice: door.closed_notice ?? null });
        if (typeof res.data?.new_families_open_on === 'string') setNewFamiliesOpenOn(res.data.new_families_open_on);
      })
      .catch(() => {
        // fail open — matches every other status check on this page; the
        // backend gate is the real enforcement.
      })
      .finally(() => {
        if (!cancelled) setGateResolved(true);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!inviteToken) return;
    stashApplyInviteToken(inviteToken);
    let cancelled = false;
    api.applications
      .inviteStatus(inviteToken)
      .then((res) => {
        if (cancelled) return;
        const valid = Boolean(res.data?.valid);
        setInviteCheck(valid ? 'ok' : 'invalid');
        // Read loosely: the field arrives only beside valid:true, and only for
        // a renewal invite (routes/applications.js GET /invite/status).
        const data = res.data as { renewal?: unknown } | undefined;
        setCredentialRenewal(valid && data?.renewal === true);
      })
      .catch(() => {
        if (!cancelled) setInviteCheck('ok'); // fail open — backend gate still enforces
      });
    return () => { cancelled = true; };
  }, [inviteToken]);

  // GH-415: mirror of the invite effect for ?code=. Stash first so the code
  // survives the signup → wizard hop, then check it. Fails OPEN on a network
  // error — the backend re-validates and redeems at draft creation.
  // #1017: the response carries one extra, deliberate outcome on top of
  // the original valid/invalid pair — `typo` (see
  // api.applications.codeStatus for what it does and does not reveal).
  // Every other invalid reason still collapses to plain 'invalid'.
  //
  // BLOCKER 1 (2026-08-23 addendum fix-wave): held behind `gateResolved` — see
  // that state's comment — and, once the mode is known, skipped ENTIRELY in
  // `disabled` mode. Spec §3 / the addendum: a `?code=` already in
  // circulation is IGNORED, not rejected, when the platform is configured not
  // to use codes at all — no validation call, no error, no consumed use. The
  // code is simply never checked, so `codeCheck` goes straight to 'ok' and the
  // family reaches the signup form exactly as if no code were present.
  useEffect(() => {
    if (!urlCode) return;
    if (!gateResolved) return;
    if (gateMode === 'disabled') {
      setCodeCheck('ok');
      return;
    }
    stashApplyCode(urlCode);
    let cancelled = false;
    api.applications
      .codeStatus(urlCode)
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        if (data?.valid) {
          setCodeCheck('ok');
          // 2026-10-28: a code with Renewal Families on skips the pre-screen,
          // the same hint the invite branch reads.
          setCredentialRenewal(data.renewal === true);
        } else if (data?.typo) {
          setCodeCheck('typo');
        } else {
          setCodeCheck('invalid');
        }
      })
      .catch(() => {
        if (!cancelled) setCodeCheck('ok'); // fail open — backend gate still enforces
      });
    return () => { cancelled = true; };
  }, [urlCode, gateResolved, gateMode]);

  // #2692: the typo stop's two ways on. Fixed in place: the entry has already
  // validated and stashed the new code, so only this page's own state moves.
  // Without a code: the bad code must leave the stash too, or createDraft
  // would send it and the server would refuse a family we just waved through.
  // #2756: the address bar moves with it — the fixed code replaces `?code=`,
  // continuing without one drops it — so a reload never brings the typo stop
  // back. `replace` keeps Back off the bad URL; other params ride along.
  const rewriteUrlCode = (code: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (code) next.set('code', code);
      else next.delete('code');
      return next;
    }, { replace: true });
  };
  // 2026-10-28: a typed renewal code skips the pre-screen, as its link does.
  const handleCodeTyped = (code: string, opts?: { renewal: boolean }) => {
    setTypedCode(code);
    setCredentialRenewal(opts?.renewal === true);
  };
  const handleCodeFixed = (code: string, opts?: { renewal: boolean }) => {
    handleCodeTyped(code, opts);
    setUrlCode(null);
    setCodeCheck('ok');
    rewriteUrlCode(code);
  };
  const handleContinueWithoutCode = () => {
    try { sessionStorage.removeItem(APPLY_CODE_STORAGE_KEY); } catch { /* nothing stashed */ }
    setUrlCode(null);
    setCodeCheck('ok');
    rewriteUrlCode(null);
  };

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ApplyFormData>({
    resolver: zodResolver(applySchema),
  });

  const onSubmit = async (data: ApplyFormData) => {
    setSubmitting(true);
    try {
      await applySignup(data, rememberMe);
      // #2401: replace, not push (the #1388 pattern) — the signup form is not
      // a Back target. Wizard steps are component state, so a push left bare
      // /apply directly behind every step.
      navigate('/apply/wizard', { replace: true });
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string } } };
      // #2693: "an account with this email already exists" belongs at the
      // Email field, in the family's language, with the way back in beside
      // it. The server's English sentence is not shown; the status is the
      // signal.
      if (e?.response?.status === 409) {
        setError('email', { type: 'duplicate', message: t('applyLanding.emailTaken') }, { shouldFocus: true });
        return;
      }
      // R3-B11: the server's sentence is English, so only an English reader
      // sees it; everyone else gets the localized fallback line.
      const english = i18n.language === 'en' || i18n.language.startsWith('en-');
      toast({
        variant: 'destructive',
        title: t('applyLanding.signupFailedTitle'),
        description: (english ? e?.response?.data?.message : undefined) || t('applyLanding.signupFailedDescription'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  // #2401: a signed-in applicant on /apply — browser Back from the wizard —
  // belongs in her application, not on a gate. Replace, so Back from the
  // wizard does not bounce here again.
  //
  // #2819: except when the wizard sent her HERE to answer the pre-screen —
  // bouncing back then was a loop on a blank page. She answers it (always
  // re-asked: the server refused whatever this tab held), the answers are
  // stashed for the create, and she returns to the wizard. Integrity is the
  // server's: POST /applications re-scores them and a miss is still a 403.
  if (isAuthenticated && user?.role === 'applicant' && prescreenRequired) {
    return (
      <EligibilityCheck
        notice={t('applyLanding.prescreenRequired')}
        onComplete={() => navigate(homeFor('applicant'), { replace: true })}
      />
    );
  }
  if (isAuthenticated && user?.role === 'applicant') {
    return <Navigate to={homeFor('applicant')} replace />;
  }

  // #2401: until the boot session check settles, a signed-in visitor would be
  // screened as anonymous — on a reload the referral stop flashed before the
  // session arrived. Same neutral spinner as the credential check below.
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" role="status" aria-label={t('applyLanding.checkingInvite')}>
        <Loader2 className="h-6 w-6 animate-spin text-stevens-indigo" />
      </div>
    );
  }

  // B6b: a present-but-no-longer-valid token stops here (flag on or off) —
  // the backend rejects it at draft creation too; this screen just says so
  // BEFORE the family creates an account. An invite is validated in every
  // mode (matches the backend's precedence — invite always wins outright), so
  // this branch has no `disabled`-mode guard.
  // L3-c (D4): the round has closed — every door, Optional mode included,
  // shows the closed stop before anyone signs up. `disabled` mode is a door
  // staff have taken out of the credential business, so it is left alone.
  if (roundClosed && gateMode !== 'disabled') {
    return <InviteRequiredStop variant="cycle-closed" notice={roundClosed.notice ?? undefined} />;
  }

  if (inviteCheck === 'invalid') {
    return <InviteRequiredStop variant="invalid" />;
  }

  // GH-415: an invalid ?code= gets the identical stop screen — EXCEPT in
  // `disabled` mode. BLOCKER 1 (2026-08-23 addendum fix-wave): in practice
  // the code-check effect above never sets `codeCheck` to 'invalid' /
  // 'typo' while `gateMode === 'disabled'` (it short-circuits
  // straight to 'ok'), but these two guards are the belt to that suspenders
  // — a stop screen must never be reachable here for a mode the platform has
  // configured not to validate codes at all.
  if (codeCheck === 'invalid' && gateMode !== 'disabled') {
    return <InviteRequiredStop variant="invalid" />;
  }

  // #1017: a mistyped ?code= gets its own honest message instead of the
  // generic 'invalid' collapse — telling a family "that's a typo" is safe
  // because the check symbol that flags it is computed offline.
  if (codeCheck === 'typo' && gateMode !== 'disabled') {
    return (
      <InviteRequiredStop
        variant="typo"
        initialCode={urlCode ?? undefined}
        onCodeAccepted={handleCodeFixed}
        onContinueWithoutCode={handleContinueWithoutCode}
      />
    );
  }

  if (inviteCheck === 'checking' || codeCheck === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center" role="status" aria-label={t('applyLanding.checkingInvite')}>
        <Loader2 className="h-6 w-6 animate-spin text-stevens-indigo" />
      </div>
    );
  }

  // #3452: before the cycle's "New families from" day only a renewing family
  // (a valid renewal link or code) may start. Everyone else is told the day
  // here, before an account, and may type a renewal code to go on. POST
  // /api/applications refuses the same families, so this is the courtesy.
  if (newFamiliesOpenOn && !credentialRenewal && !(isAuthenticated && user?.role === 'applicant')) {
    return (
      <InviteRequiredStop
        variant="new-families-soon"
        newFamiliesOpenOn={newFamiliesOpenOn}
        onCodeAccepted={handleCodeTyped}
      />
    );
  }

  // GH-271.1: an authenticated session (a TRFer "just checking" an /apply
  // link, a parent already in the app, …) must never be silently replaced by
  // a brand-new applicant identity. Confirm intent first — the ONLY path to
  // the signup form while signed in is the explicit log-out action.
  // #2401: this runs BEFORE the referral gate and the eligibility check — a
  // signed-in visitor is past both — and only an invalid credential in the
  // URL (the stops above) still shows first, because it says something true
  // about the link being checked.
  if (isAuthenticated) {
    const handleLogoutAndStart = async () => {
      setLoggingOut(true);
      try {
        await logout();
      } finally {
        // logout() ends on /login — bring the user straight back to the
        // application page (keeping any invite token, or GH-415 code) so the
        // form is waiting.
        const returnTo = inviteToken
          ? `/apply?invite=${encodeURIComponent(inviteToken)}`
          : urlCode
            ? `/apply?code=${encodeURIComponent(urlCode)}`
            : '/apply';
        navigate(returnTo, { replace: true });
      }
    };

    return (
      <AuthShell
        documentLabel={t('applyChrome.applyToTrf')}
        meta={[
          { k: t('applyLanding.signedIn.metaSignedInAs'), v: user?.email ?? t('applyLanding.signedIn.yourAccount') },
          { k: t('applyLanding.signedIn.metaNewApplication'), v: t('applyLanding.signedIn.metaNewApplicationValue') },
        ]}
      >
        <div className="mb-5">
          <MonoLabel>{t('applyLanding.signedIn.eyebrow')}</MonoLabel>
        </div>

        <h1
          className="text-[clamp(28px,3.6vw,40px)] leading-[1.15] tracking-tight text-stevens-ink mb-4 font-serif"
        >
          {t('applyLanding.signedIn.title')}
        </h1>

        <p className="text-lede leading-relaxed text-stevens-ink-2 mb-3 max-w-[440px]">
          <Trans
            t={t}
            i18nKey="applyLanding.signedIn.lede"
            values={{ email: user?.email ?? t('applyLanding.signedIn.yourAccount') }}
            components={{ strong: <strong className="text-stevens-ink" /> }}
          />
        </p>
        <p className="text-lede leading-relaxed text-stevens-ink-2 mb-8 max-w-[440px]">
          {t('applyLanding.signedIn.ledeOnBehalf')}
        </p>

        {renewalDue && (
          <p className="text-lede leading-relaxed text-stevens-ink-2 mb-8 max-w-[440px]" data-testid="apply-signed-in-renewal">
            {t('applyLanding.signedIn.renewalLede')}
          </p>
        )}

        <div className="flex flex-col gap-3 sm:flex-row">
          {/* #2693: when her renewal is due it leads; "Back to my account"
              steps down to the secondary style beside it. */}
          {renewalDue && (
            <button
              type="button"
              onClick={() => navigate('/renew')}
              className="flex items-center justify-center rounded-sharp bg-stevens-indigo-solid px-6 py-3 text-body font-medium text-stevens-indigo-solid-fg transition-colors hover:bg-stevens-indigo-solid-2"
            >
              {t('applyLanding.signedIn.startRenewal')}
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(homeFor(user?.role), { replace: true })}
            className={renewalDue
              ? 'flex items-center justify-center rounded-sharp border border-stevens-indigo/30 px-6 py-3 text-body font-medium text-stevens-indigo transition-colors hover:bg-stevens-indigo/5'
              : 'flex items-center justify-center rounded-sharp bg-stevens-indigo-solid px-6 py-3 text-body font-medium text-stevens-indigo-solid-fg transition-colors hover:bg-stevens-indigo-solid-2'}
          >
            {t('applyLanding.signedIn.back')}
          </button>
          <button
            type="button"
            onClick={handleLogoutAndStart}
            disabled={loggingOut}
            className="flex items-center justify-center gap-2 rounded-sharp border border-stevens-indigo/30 px-6 py-3 text-body font-medium text-stevens-indigo transition-colors hover:bg-stevens-indigo/5 disabled:opacity-60"
          >
            {loggingOut && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('applyLanding.signedIn.signOutAndStart')}
          </button>
        </div>

        <p className="mt-10 text-caption text-stevens-ink-2/60">
          {t('applyChrome.footer')}
        </p>
      </AuthShell>
    );
  }

  // B6 (F-013): with gateMode === 'required' and no token in the URL, show the
  // warm referral stop screen instead of the signup form. Cosmetic — the
  // backend gate in routes/applications.js POST / is the enforcement.
  // #582 / ledger D-2 (2026-08-11), superseded 2026-08-23 by the #1017
  // addendum: production still defaults `application_codes_mode` to
  // `required` — intake is referral-only, because that is TRF's actual
  // program — but it is now a staff-settable runtime value (#1018), not a
  // frozen deploy-time flag. This branch runs for every family arriving
  // without a credential while the mode is `required`, which is why the stop
  // screen carries real links to trf.org/get-support and the partner-hospitals
  // page rather than a bare "contact TRF" sentence.
  // GH-415: the stop screen also accepts a typed application code, so a family
  // who was given a code verbally (no link) can unlock the form right here.
  if (gateMode === 'required' && !inviteToken && !urlCode && !typedCode) {
    return (
      <InviteRequiredStop
        variant="missing"
        onCodeAccepted={handleCodeTyped}
        onReferralChange={stashApplyReferral}
      />
    );
  }

  // F-6 (#330 / F-038, D-6f): screen for TRF's published eligibility criteria
  // BEFORE any account or draft can exist — after the invite/code gate and
  // the authenticated-session confirm above (#2401), before the signup form.
  // D-FB-3 (2026-08-20, supersedes D-6b): the check is now a HARD STOP — a
  // family that misses stays on the result panel (re-answering is possible;
  // nothing is saved). Only 'passed' and 'skipped' reach onComplete and set
  // the gate; there is no continue-past-miss outcome anymore.
  // 2026-10-28: a valid renewal link or code skips the checklist entirely.
  if (eligibilityGate === null && !credentialRenewal) {
    return (
      <EligibilityCheck
        codeEntry={gateMode === 'optional' && !inviteToken && !urlCode && !typedCode ? (
          <ApplicationCodeEntry
            onCodeAccepted={(code, opts) => {
              setCodeBeforeScreen(true);
              handleCodeTyped(code, opts);
            }}
          />
        ) : undefined}
        onComplete={(outcome) => {
          if (typeof window !== 'undefined') {
            window.sessionStorage.setItem(ELIGIBILITY_GATE_KEY, outcome);
          }
          setEligibilityGate(outcome);
        }}
      />
    );
  }

  return (
    <AuthShell
      documentLabel={t('applyChrome.applyToTrf')}
      meta={[
        { k: t('applyChrome.estTime'), v: t('applyLanding.meta.estTimeValue') },
        { k: t('applyLanding.meta.saveAnytime'), v: t('applyLanding.meta.saveAnytimeValue') },
        { k: t('applyLanding.meta.review'), v: t('applyLanding.meta.reviewValue') },
      ]}
      alternate={{
        prompt: t('applyLanding.alternatePrompt'),
        to: '/login',
        label: t('applyLanding.alternateSignIn'),
      }}
    >
      {/* Eyebrow. The language switcher is AuthShell's, at the top of the
          aside (#2109 R1 L4); a second one here showed two on /apply (#2696). */}
      <div className="mb-5">
        <MonoLabel>{t('applyChrome.applyToTrf')}</MonoLabel>
      </div>

      {/* Headline */}
      <h1
        className="text-[clamp(28px,3.6vw,40px)] leading-[1.15] tracking-tight text-stevens-ink mb-4 font-serif"
      >
        <Trans
          t={t}
          i18nKey="applyLanding.title"
          components={{ br: <br className="hidden sm:block" />, em: <em /> }}
        />
      </h1>

      {/* Lede */}
      <p className="text-lede leading-relaxed text-stevens-ink-2 mb-8 max-w-[420px]">
        {t('applyLanding.lede')}
      </p>

      {/* What to expect */}
      <div className="mb-10 border-l-2 border-stevens-indigo/20 pl-5 flex flex-col gap-3">
        {(['minutes', 'save', 'review'] as const).map((point) => (
          <p key={point} className="text-body text-stevens-ink-2 leading-snug">
            {t(`applyLanding.points.${point}`)}
          </p>
        ))}
      </div>

      {/* #1018 Task 15: `optional`/`disabled` mode reaches this signup form
          directly — no stop screen — so the referral question has to live
          here instead, or it is never asked at all. The stop screen means
          "you cannot proceed"; in these two modes the family CAN, so
          InviteRequiredStop is the wrong vehicle for it. Placed directly
          above the form, same relative position InviteRequiredStop uses for
          its own copy of this block.
          `disabled` mode omits the code entry — staff have turned codes off
          for this cycle, so offering a code box would be incoherent — but
          still asks who referred the family, since that has nothing to do
          with whether a code exists. `required` mode never reaches this
          return with a bare gate (it either stops on InviteRequiredStop or
          arrives here already carrying a token/code that identifies the
          referral), so it is intentionally excluded below. */}
      {(gateMode === 'optional' || gateMode === 'disabled') && (
        <>
          {gateMode === 'optional' && !codeBeforeScreen && (
            <ApplicationCodeEntry onCodeAccepted={setTypedCode} />
          )}
          <ReferralBlock onChange={stashApplyReferral} />
        </>
      )}

      {/* Signup form */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
        <EditorialField
          label={t('applyLanding.fields.firstName')}
          id="first_name"
          autoComplete="given-name"
          error={errors.first_name?.message}
          {...register('first_name')}
        />

        <EditorialField
          label={t('applyLanding.fields.email')}
          id="email"
          type="email"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
        />
        {/* #2693: indigo, underlined, focus ring, 44 px touch target: links, not the error sentence above. */}
        {errors.email?.type === 'duplicate' && (
          <p className="-mt-2 flex flex-wrap gap-x-5 gap-y-1 text-body-sm" data-testid="email-taken-links">
            <Link to="/login" className="rounded-sm text-stevens-indigo underline underline-offset-4 hover:text-stevens-indigo-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring [@media(pointer:coarse)]:-my-3 [@media(pointer:coarse)]:inline-flex [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:items-center">
              {t('applyLanding.alternateSignIn')}
            </Link>
            <Link to="/forgot-password" className="rounded-sm text-stevens-indigo underline underline-offset-4 hover:text-stevens-indigo-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring [@media(pointer:coarse)]:-my-3 [@media(pointer:coarse)]:inline-flex [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:items-center">
              {t('applyLanding.emailTakenForgot')}
            </Link>
          </p>
        )}

        <EditorialField
          label={t('applyLanding.fields.password')}
          id="password"
          type="password"
          autoComplete="new-password"
          error={errors.password?.message}
          {...register('password')}
        />

        <label className="flex items-center gap-2.5 select-none cursor-pointer">
          <input
            id="rememberMe"
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="h-4 w-4 rounded border-stevens-rule text-stevens-indigo focus:ring-2 focus:ring-stevens-indigo/30 focus:ring-offset-0"
          />
          <span className="text-body text-stevens-ink-2">{tAuth('login.rememberMe')}</span>
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="mt-2 flex items-center justify-center gap-2 rounded-sharp bg-stevens-indigo-solid px-6 py-3 text-body font-medium text-stevens-indigo-solid-fg transition-colors hover:bg-stevens-indigo-solid-2 disabled:opacity-60"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('applyChrome.applyToTrf')}
        </button>
      </form>

      {/* Footer attribution */}
      <p className="mt-10 text-caption text-stevens-ink-2/60">
        {t('applyChrome.footer')}
      </p>
    </AuthShell>
  );
}
